"use client";

import { twMerge } from "tailwind-merge";
import ActionButton from "@repo/ui/action-button";
import { getNpsThemeClasses } from "@repo/nps/branded";
import { BodyText, HeadlineText } from "@repo/ui/typography";
import { NPS_SCORES } from "../constants";
import type { NpsCopy } from "../types";

interface ScoreSelectionProps {
  copy: NpsCopy;
  selectedScore: number | null;
  onScoreSelect: (score: number) => void;
  onSubmit: () => void;
  onNotNow: () => void;
}

export function ScoreSelection({
  copy,
  selectedScore,
  onScoreSelect,
  onSubmit,
  onNotNow,
}: ScoreSelectionProps) {
  const scores = Array.from({ length: NPS_SCORES.TOTAL_COUNT }, (_, i) => i);
  const themeClasses = getNpsThemeClasses();

  return (
    <div className="flex flex-col gap-[24px] items-center w-full">
      {/* Question */}
      <div className="flex flex-col gap-[8px] items-center w-full">
        <HeadlineText
          className={twMerge(
            themeClasses.title,
            "text-left text-[20px] leading-[28px] tracking-[0.2px] w-full"
          )}
        >
          {copy.question}
        </HeadlineText>
        {copy.description && (
          <BodyText
            className={twMerge(
              themeClasses.description,
              "text-left text-[15px] leading-[22px] tracking-[0.25px] w-full"
            )}
          >
            {copy.description}
          </BodyText>
        )}
      </div>

      {/* Score Buttons Grid */}
      <div className="grid grid-cols-11 gap-[1px] w-full">
        {scores.map((score) => {
          const isFirst = score === NPS_SCORES.MIN;
          const isLast = score === NPS_SCORES.MAX;
          const roundedClass = isFirst
            ? "rounded-l-[8px]"
            : isLast
              ? "rounded-r-[8px]"
              : "rounded-none";

          const buttonClass = twMerge(
            "h-[48px] flex items-center justify-center text-[16px] font-medium leading-[24px] transition-all duration-200 border-2",
            roundedClass,
            selectedScore === score
              ? themeClasses.scoreSelected
              : themeClasses.buttonUnselected
          );

          return (
            <button
              key={score}
              type="button"
              onClick={() => onScoreSelect(score)}
              className={buttonClass}
              aria-label={`Select score ${score}`}
            >
              {score}
            </button>
          );
        })}
      </div>

      {/* Scale Labels */}
      <div className="flex justify-between items-center w-full -mt-[16px]">
        <BodyText
          className={twMerge(
            themeClasses.label,
            "text-[13px] leading-[20px] tracking-[0.25px]"
          )}
        >
          Not at all likely
        </BodyText>
        <BodyText
          className={twMerge(
            themeClasses.label,
            "text-[13px] leading-[20px] tracking-[0.25px]"
          )}
        >
          Extremely likely
        </BodyText>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-[16px] items-center w-full">
        <ActionButton
          text="Submit"
          onPress={onSubmit}
          eventData={{
            action: "nps_score_submit",
          }}
          disabled={selectedScore === null}
          variant="primary"
        />
        <button
          type="button"
          onClick={onNotNow}
          className="h-[44px] w-full rounded-[99px] flex items-center justify-center px-[24px] py-[14px]"
        >
          <BodyText className={themeClasses.tertiary}>Not now</BodyText>
        </button>
      </div>
    </div>
  );
}
